import { tagBackgroundClass } from "./tag-colors";
import { format, startOfWeek, endOfWeek, addDays, isSameDay, addHours, startOfDay } from "date-fns"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import type { CalendarEvent } from "./calendar"
import UserSvg from "@/assets/userSvg.svg"
import { CalendarEventActions } from "./calendar-event-actions"
import { useState, useMemo } from "react"

interface WeekViewProps {
  currentDate: Date
  events: CalendarEvent[]
  dateRange: { start: Date; end: Date }
  onViewEvent?: (event: CalendarEvent) => void
  onEditEvent?: (event: CalendarEvent) => void
  onDeleteEvent?: (event: CalendarEvent) => void
  refetch?: () => void
  access?: string
  hideEventActions?: boolean
  onDateClick?: (date: Date) => void
}

// Map color to static Tailwind classes
const getColorClasses = (color?: string) => {
  const colorMap: Record<string, string> = {
    purple: "bg-purple-200 border-purple-400 text-purple-900 hover:bg-purple-250",
    blue: "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250",
    green: "bg-green-200 border-green-400 text-green-900 hover:bg-green-250",
    red: "bg-red-200 border-red-400 text-red-900 hover:bg-red-250",
    yellow: "bg-yellow-200 border-yellow-400 text-yellow-900 hover:bg-yellow-250",
    orange: "bg-orange-200 border-orange-400 text-orange-900 hover:bg-orange-250",
    teal: "bg-teal-200 border-teal-400 text-teal-900 hover:bg-teal-250",
    pink: "bg-pink-200 border-pink-400 text-pink-900 hover:bg-pink-250",
    indigo: "bg-indigo-200 border-indigo-400 text-indigo-900 hover:bg-indigo-250",
    gray: "bg-gray-200 border-gray-400 text-gray-900 hover:bg-gray-250",
  };
  return colorMap[color || ""] || "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250";
};

// Component to render a single event
const EventCard = ({
  event,
  onViewEvent,
  onEditEvent,
  onDeleteEvent,
  refetch,
  access,
  style,
  compact = false,
  hideEventActions
}: {
  event: CalendarEvent;
  onViewEvent?: (event: CalendarEvent) => void;
  onEditEvent?: (event: CalendarEvent) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  refetch?: () => void;
  access?: string;
  style?: React.CSSProperties;
  compact?: boolean;
  hideEventActions?: boolean;
}) => (
  <div
    className={cn(
      "rounded-md border overflow-hidden shadow-sm transition-colors",
      compact ? "p-0.5 sm:p-1" : "p-1 sm:p-2",
      getColorClasses(event.color),
      onViewEvent && "cursor-pointer"
    )}
    style={style}
    onClick={(e) => {
      e.stopPropagation();
      onViewEvent?.(event);
    }}
  >
    <div className="flex items-start justify-between gap-1">
      <div
        className={cn(
          "font-medium truncate flex-1 cursor-pointer",
          compact ? "text-[10px]" : "text-xs"
        )}
        onClick={(e) => {
          e.stopPropagation();
          onViewEvent?.(event);
        }}
      >
        {event.title}
      </div>
      {!hideEventActions && (
        <CalendarEventActions
          event={event}
          onEdit={onEditEvent}
          onView={onViewEvent}
          onDelete={onDeleteEvent}
          refetch={refetch}
          access={access}
          size="sm"
          variant="dropdown"
        />
      )}
    </div>
    {event.tags && !compact && (
      <div className="flex flex-wrap gap-1 mt-1">
        {event.tags.slice(0, 2).map((tag, idx) => (
          <span
            key={idx}
            className={cn(
              "px-1.5 py-0.5 rounded-sm text-white text-[10px]",
              tagBackgroundClass(tag.color)
            )}
          >
            {tag.label}
          </span>
        ))}
        {event.tags.length > 2 && (
          <span className="text-[10px] text-gray-500">+{event.tags.length - 2}</span>
        )}
      </div>
    )}
    {event.tags && compact && (
      <div className="flex gap-1 mt-0.5">
        {event.tags.slice(0, 1).map((tag, idx) => (
          <span
            key={idx}
            className={cn(
              "px-1 py-0.5 rounded-sm text-white text-[8px]",
              tagBackgroundClass(tag.color)
            )}
          >
            {tag.label}
          </span>
        ))}
      </div>
    )}
  </div>
);

export function WeekView({ currentDate, events, dateRange, onViewEvent, onEditEvent, onDeleteEvent, refetch, access, hideEventActions, onDateClick }: WeekViewProps) {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ day: Date; hour: number; events: CalendarEvent[] } | null>(null);
  const startDate = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start from Monday to match slot generation
  const endDate = endOfWeek(startDate, { weekStartsOn: 1 });

  const hours = Array.from({ length: 24 }, (_, i) => i); // 0:00 to 23:00
  const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));


  // Pre-index events by `${yyyy-MM-dd}_${hour}` for O(1) instant lookup
  const eventsByDayHour = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const startVal = event.start;
      const d = startVal instanceof Date ? startVal : new Date(startVal);
      if (isNaN(d.getTime())) continue;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dayStr = String(d.getDate()).padStart(2, "0");
      const key = `${y}-${m}-${dayStr}_${d.getHours()}`;
      let list = map.get(key);
      if (!list) {
        list = [];
        map.set(key, list);
      }
      list.push(event);
    }
    return map;
  }, [events]);

  // Get events for a specific day and hour in O(1)
  const getEventsForTimeSlot = (day: Date, hour: number) => {
    const y = day.getFullYear();
    const m = String(day.getMonth() + 1).padStart(2, "0");
    const dayStr = String(day.getDate()).padStart(2, "0");
    return eventsByDayHour.get(`${y}-${m}-${dayStr}_${hour}`) || [];
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      <div className="sticky top-0 z-10 grid grid-cols-8 border-b bg-white shrink-0">
        <div className="border-r py-2 text-center text-xs"></div>
        {days.map((day, index) => (
          <div
            key={day.toString()}
            className={cn(
              "py-2 text-center text-sm border-r",
              // index === 3 && "bg-green-50/30" // Highlight Wednesday like in the screenshot
            )}
          >
            <p className="text-[10px] sm:text-xs"><span className="sm:hidden">{format(day, "EEE")}</span><span className="hidden sm:inline">{format(day, "EEEE")}</span></p>
            <p className="text-[10px] sm:text-[11px]">{format(day, "MMM d")}</p>
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col">
          {hours.map((hour) => (
            <div key={hour} className="grid grid-cols-8 border-b min-h-[4rem] sm:min-h-[6rem] shrink-0 h-auto">
              <div className="sticky left-0 z-10 border-r bg-white px-1 sm:px-2 py-1 text-xs text-gray-500">
                <div className="sticky top-1">{hour}:00</div>
              </div>
              
              {days.map((day) => {
                const eventsForSlot = getEventsForTimeSlot(day, hour);

                return (
                  <div
                    key={day.toString()}
                    className="border-r p-0.5 sm:p-1 hover:bg-gray-50/50 cursor-pointer flex flex-col gap-1"
                    onClick={() => {
                      const clickedDate = addHours(startOfDay(day), hour);
                      onDateClick?.(clickedDate);
                    }}
                  >
                    {eventsForSlot.map((event, index) => (
                      <EventCard
                        key={event.id || index}
                        event={event}
                        onViewEvent={onViewEvent}
                        onEditEvent={onEditEvent}
                        onDeleteEvent={onDeleteEvent}
                        refetch={refetch}
                        access={access}
                        hideEventActions={hideEventActions}
                        compact={true}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

